/**
 * Profile native video service.
 *
 * Talks to the native pipeline at /api/profiles/:userId/videos/* -- the same
 * GCS-upload -> branded-intro -> Cloudflare Stream machinery rituals use. Two-
 * step upload (mint signed URL, resumable PUT to GCS, then confirm) and a
 * signed playback token (profile playback is always signed).
 */

import apiClient from "@/lib/api-client"

export type ProfileVideoStatus = "pending" | "processing" | "ready" | "failed"

/** One native profile video row as projected by the backend list endpoint. */
export interface ProfileVideoItem {
  id: string
  userId: string
  status: ProfileVideoStatus
  cloudflareUid: string | null
  duration: number | null
  thumbnailUrl: string | null
  title: string | null
  sortOrder: number
  /** Only populated for the profile owner. */
  failureReason: string | null
  createdAt: string
  updatedAt: string
}

export interface RequestUploadUrlInput {
  filename: string
  contentType: string
  sizeBytes: number
  durationSeconds: number
}

export interface RequestUploadUrlResponse {
  signedUrl: string
  gcsPath: string
  contentType: string
  maxBytes: number
  expiresAt: number
}

export interface ConfirmUploadInput {
  gcsPath: string
  sortOrder?: number
  title?: string | null
}

export interface ConfirmUploadResponse {
  profileVideoId: string
  status: ProfileVideoStatus
  sortOrder: number
  gcsPath: string
}

export interface PlaybackTokenResponse {
  /** Signed JWT (profile playback is always signed). */
  token: string | null
  expiresAt: number | null
  cloudflareUid: string
}

export const requestProfileVideoUploadUrl = async (
  userId: string,
  input: RequestUploadUrlInput,
): Promise<RequestUploadUrlResponse> => {
  const { data } = await apiClient.post<{ success: boolean; data: RequestUploadUrlResponse }>(
    `api/profiles/${userId}/videos/upload-url`,
    input,
    { timeout: 30000 },
  )
  return data.data
}

export const confirmProfileVideoUpload = async (
  userId: string,
  input: ConfirmUploadInput,
): Promise<ConfirmUploadResponse> => {
  const { data } = await apiClient.post<{ success: boolean; data: ConfirmUploadResponse }>(
    `api/profiles/${userId}/videos`,
    input,
    { timeout: 30000 },
  )
  return data.data
}

export const listProfileVideos = async (userId: string): Promise<ProfileVideoItem[]> => {
  const { data } = await apiClient.get<{ success: boolean; data: ProfileVideoItem[] }>(
    `api/profiles/${userId}/videos`,
  )
  return data.data
}

export const getProfileVideoPlaybackToken = async (
  userId: string,
  videoId: string,
): Promise<PlaybackTokenResponse> => {
  const { data } = await apiClient.get<{ success: boolean; data: PlaybackTokenResponse }>(
    `api/profiles/${userId}/videos/${videoId}/playback-token`,
  )
  return data.data
}

export const deleteProfileVideo = async (userId: string, videoId: string): Promise<void> => {
  await apiClient.delete(`api/profiles/${userId}/videos/${videoId}`)
}
